import { useCallback, useMemo, useRef, useState } from "react";
import type { FlatList, NativeScrollEvent, NativeSyntheticEvent } from "react-native";

import { isFollowingInverted } from "@/lib/chat-follow";
import { probe } from "@/lib/timing-probe";

/**
 * Keep an INVERTED message thread pinned to its newest message, without ever
 * taking the scroll away from a reader who has moved it themselves.
 *
 * The wiring half of `chat-follow.ts`; the rules and the measurements that
 * produced them live there. The list this serves renders newest-first inside
 * `inverted`, which is what makes this hook small. Its previous version — for
 * a normal list whose newest message lived at the bottom — needed a settle
 * window, a pending flag and a bounded confirm loop, because `scrollToEnd`
 * resolves "the end" to a pixel at call time and the end kept moving: rows
 * still rendering, a burst of arrivals, our own animation poisoning the
 * follow flag. Every one of those was a compensation for aiming at a moving
 * target. The newest message of an inverted thread sits at offset 0, and 0
 * never moves, so the machinery went with the problem.
 *
 * What remains, and why each piece is still here:
 *
 *  - **`onScroll` tracks the reader.** Within `FOLLOW_THRESHOLD_PX` of the
 *    newest message counts as following; past it, arrivals leave them alone.
 *    Our own `scrollToOffset(0, animated: false)` cannot poison this the way
 *    the old animated travel did — it emits no journey, only the destination,
 *    and the destination reports as following.
 *  - **`onContentSizeChange` closes the anchoring gap.** A reader NEAR the
 *    newest message but not exactly on it is held in place by scroll
 *    anchoring when a message arrives (measured: the browser adjusts the
 *    offset to the pixel; native `maintainVisibleContentPosition` does the
 *    same). Without this snap, each arrival would walk a following reader one
 *    bubble further from the end until they silently stopped following.
 *  - **`follow` is the reader's own ask** — they sent something, and writing
 *    into a thread IS asking to see it, wherever they had scrolled.
 *
 * **Per mounted screen, and that is load-bearing.** `followingRef` starts
 * fresh with the component, which is correct only because a conversation is
 * always reached by `router.push` — a new instance every time. The same
 * assumption makes the one-to-one thread's `useState(() => cachedMessages(id))`
 * seed correct, and `chat-screen-remount.test.ts` enforces it.
 */
export function useThreadFollow<T>(listRef: React.RefObject<FlatList<T> | null>) {
  // Optimistic: a thread with nothing in it yet is, trivially, at its own
  // newest message.
  const followingRef = useRef(true);
  // The ref's render-visible mirror, and both are needed: `onScroll` must
  // read/write synchronously without a render per scroll event, while the
  // `maintainVisibleContentPosition` value below has to reach the list as a
  // PROP. `setFollowing` bails out on an unchanged value, so re-renders are
  // bounded by threshold crossings, not by scroll traffic.
  const [following, setFollowing] = useState(true);

  /**
   * Never animated. A landing on the newest message is a position, not a
   * journey — the device measurement in `chat-follow.ts` (13.89% janky frames
   * animated against 3.26% not) predates the inversion and still holds.
   */
  const snapToNewest = useCallback(() => {
    listRef.current?.scrollToOffset({ offset: 0, animated: false });
  }, [listRef]);

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      followingRef.current = isFollowingInverted(event.nativeEvent.contentOffset.y);
      setFollowing(followingRef.current);
    },
    [],
  );

  const onContentSizeChange = useCallback((_width: number, height: number) => {
    // The HEIGHT is the whole question for the send shake: if the thread's
    // content measures the same before and after a message settles, nothing
    // can have moved, whatever else fires. Without it this probe only says
    // "something changed" — and keyboard animations change it too, which is
    // what made the build-14 numbers so hard to read.
    if (followingRef.current) {
      // Measurement for the send-paint prerequisite (`message-outbox-plan.md`
      // §1): this is the JS-side correction whose lateness relative to the
      // anchored native paint is the filmed flash. Its timestamp against the
      // `--bugreport` frame clock is what attributes — or clears — it.
      probe("thread.sizeChange.snap", { h: Math.round(height) });
      snapToNewest();
    }
  }, [snapToNewest]);

  /**
   * The reader asked to be at the newest message — because they just sent
   * something. Their own message is the one case where "they scrolled up, so
   * leave them alone" is wrong: without this, a reader who had scrolled up and
   * then sent a message watched it land far below them, with nothing on screen
   * to say it had sent at all.
   */
  const follow = useCallback(() => {
    probe("thread.follow");
    followingRef.current = true;
    setFollowing(true);
    snapToNewest();
  }, [snapToNewest]);

  /**
   * Anchor only a reader who has scrolled away; never anchor a follower.
   *
   * The filmed send flash (`message-outbox-plan.md` §1): at offset 0 an
   * inverted list needs NO scroll to show an insert at index zero — content
   * grows, the offset stays 0, the bubble is simply there. With
   * `minIndexForVisible: 0` always on, native anchored the previously-newest
   * row instead, shifting the offset by the new row's height, and that frame
   * painted before the `onContentSizeChange` correction could run. So the
   * sender watched their bubble land low and then settle.
   *
   * A follower gives up nothing by dropping the anchor: the JS snap above
   * still corrects the near-but-not-exactly-zero case the hook comment
   * describes, and an at-zero insert now needs no correction at all. A reader
   * who scrolled away keeps the anchor and is never shifted by an arrival —
   * the promise `chat-follow.test.ts` holds both screens to.
   *
   * The prop flip and a send's row insert land in the same React commit;
   * whether the native side applies them in one mount pass is exactly what
   * the device acceptance test proves (first painted frame already final —
   * plan §1). The disqualified alternatives — reordering the calls,
   * `autoscrollToTopThreshold` — are recorded there with the evidence.
   *
   * One narrow window, accepted and named: between scrolling away and this
   * prop reaching native, an arrival could shift the reader once. The flip
   * happens on the first scroll event past the threshold, so the window is a
   * frame or two — against the every-send flash it replaces.
   */
  const maintainVisibleContentPosition = useMemo(
    () => (following ? null : { minIndexForVisible: 0 }),
    [following],
  );

  return { onScroll, onContentSizeChange, follow, maintainVisibleContentPosition };
}
